import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, FileText, Car, Settings, LogOut, Menu, X,
  Plane, Calendar, MapPin, Star, Building2, PartyPopper, 
  Edit2, Trash2, Plus, Save, Check, Clock, XCircle, Eye, Upload, Image
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconOptions = ['Plane', 'Calendar', 'MapPin', 'Star', 'Building2', 'PartyPopper'];
const iconMap = { Plane, Calendar, MapPin, Star, Building2, PartyPopper };

const AdminDashboard = () => {
  const { admin, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('quotes');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Data states
  const [quotes, setQuotes] = useState([]);
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [content, setContent] = useState(null);
  
  // Edit states
  const [editingService, setEditingService] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingContent, setEditingContent] = useState(false);
  const [contentForm, setContentForm] = useState({});
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  
  // Loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [quotesRes, servicesRes, vehiclesRes, contentRes] = await Promise.all([
        axios.get(`${API}/quotes`, getAuthHeaders()),
        axios.get(`${API}/services`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/content`)
      ]);
      setQuotes(quotesRes.data);
      setServices(servicesRes.data);
      setVehicles(vehiclesRes.data);
      setContent(contentRes.data);
      setContentForm(contentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };
  
  // Upload image to Cloudinary via backend
  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Return the Cloudinary URL directly
      return response.data.url;
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.detail || 'Erreur lors de l\'upload');
      return null;
    } finally {
      setUploading(false);
    }
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const url = await uploadImage(file);
    if (url && editingVehicle) {
      setEditingVehicle({ ...editingVehicle, image_url: url });
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  // Quote status management
  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}/status`, { status }, getAuthHeaders());
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status } : q));
    } catch (error) {
      console.error('Error updating quote:', error);
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, getAuthHeaders());
      setQuotes(quotes.filter(q => q.id !== quoteId));
    } catch (error) {
      console.error('Error deleting quote:', error);
    }
  };

  // Service management
  const saveService = async (service) => {
    try {
      if (service.id && !service.isNew) {
        await axios.put(`${API}/services/${service.id}`, service, getAuthHeaders());
      } else {
        const { isNew, ...serviceData } = service;
        await axios.post(`${API}/services`, serviceData, getAuthHeaders());
      }
      await fetchAllData();
      setEditingService(null);
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`, getAuthHeaders());
      setServices(services.filter(s => s.id !== serviceId));
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  // Vehicle management
  const saveVehicle = async (vehicle) => {
    try {
      if (vehicle.id && !vehicle.isNew) {
        await axios.put(`${API}/vehicles/${vehicle.id}`, vehicle, getAuthHeaders());
      } else {
        const { isNew, ...vehicleData } = vehicle;
        await axios.post(`${API}/vehicles`, vehicleData, getAuthHeaders());
      }
      await fetchAllData();
      setEditingVehicle(null);
    } catch (error) {
      console.error('Error saving vehicle:', error);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, getAuthHeaders());
      setVehicles(vehicles.filter(v => v.id !== vehicleId));
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  };

  // Content management
  const saveContent = async () => {
    try {
      await axios.put(`${API}/content`, contentForm, getAuthHeaders());
      setContent(contentForm);
      setEditingContent(false);
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  const statusColors = {
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
    completed: 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  const statusLabels = {
    new: 'Nouveau',
    contacted: 'Contacté',
    confirmed: 'Confirmé',
    completed: 'Terminé',
    cancelled: 'Annulé'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020202] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020202] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] border-r border-white/10 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-white/10">
          <a href="/" className="text-xl font-bold text-gold" data-testid="admin-logo">
            <span className="font-['Playfair_Display']">YENO</span>
            <span className="font-light text-white/80 ml-2">Admin</span>
          </a>
        </div>
        
        <nav className="p-4 space-y-2">
          <button
            onClick={() => { setActiveTab('quotes'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === 'quotes' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
            data-testid="nav-quotes"
          >
            <FileText size={18} />
            <span>Demandes de devis</span>
            {quotes.filter(q => q.status === 'new').length > 0 && (
              <span className="ml-auto bg-[#D4AF37] text-black text-xs px-2 py-0.5 rounded-full">
                {quotes.filter(q => q.status === 'new').length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => { setActiveTab('services'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === 'services' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
            data-testid="nav-services"
          >
            <LayoutDashboard size={18} />
            <span>Services</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('vehicles'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === 'vehicles' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
            data-testid="nav-vehicles"
          >
            <Car size={18} />
            <span>Véhicules</span>
          </button>
          
          <button
            onClick={() => { setActiveTab('content'); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${activeTab === 'content' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
            data-testid="nav-content"
          >
            <Settings size={18} />
            <span>Contenu du site</span>
          </button>
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-semibold">
              {admin?.name?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.name}</p>
              <p className="text-xs text-white/40 truncate">{admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-0">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-40 bg-[#0A0A0A] border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-white p-2" data-testid="mobile-sidebar-toggle">
            <Menu size={24} />
          </button>
          <span className="font-['Playfair_Display'] text-gold font-bold">YENO Admin</span>
          <div className="w-10"></div>
        </header>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <div className="p-6 lg:p-8">
          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <div data-testid="quotes-section">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold font-['Playfair_Display']">Demandes de devis</h1>
                <a href="/" target="_blank" className="btn-outline text-xs py-2 px-4 flex items-center gap-2" data-testid="view-site-btn">
                  <Eye size={14} />
                  Voir le site
                </a>
              </div>
              
              {quotes.length === 0 ? (
                <div className="card-dark p-12 text-center">
                  <FileText className="mx-auto mb-4 text-white/20" size={48} />
                  <p className="text-white/40">Aucune demande de devis pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <div key={quote.id} className="card-dark p-6" data-testid={`quote-${quote.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-xl font-semibold font-['Playfair_Display']">{quote.name}</h3>
                          <p className="text-white/40 text-sm">{quote.email} • {quote.phone}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <select
                            value={quote.status}
                            onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                            className={`px-3 py-1.5 border text-sm font-medium ${statusColors[quote.status]} bg-transparent cursor-pointer`}
                            data-testid={`quote-status-${quote.id}`}
                          >
                            {Object.entries(statusLabels).map(([value, label]) => (
                              <option key={value} value={value} className="bg-[#121212] text-white">{label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => deleteQuote(quote.id)}
                            className="p-2 text-red-400 hover:bg-red-500/10 transition-colors"
                            data-testid={`delete-quote-${quote.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-white/40 block">Service</span>
                          <span>{quote.service_type}</span>
                        </div>
                        <div>
                          <span className="text-white/40 block">Passagers</span>
                          <span>{quote.passengers}</span>
                        </div>
                        <div>
                          <span className="text-white/40 block">Véhicule</span>
                          <span>{quote.vehicle_preference || 'Aucune préférence'}</span>
                        </div>
                        <div>
                          <span className="text-white/40 block">Date & Heure</span>
                          <span>{quote.date} à {quote.time}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-white/40 block">Départ</span>
                          <span>{quote.pickup_location}</span>
                        </div>
                        <div>
                          <span className="text-white/40 block">Destination</span>
                          <span>{quote.dropoff_location}</span>
                        </div>
                      </div>
                      
                      {quote.message && (
                        <div className="mt-4 pt-4 border-t border-white/10 text-sm">
                          <span className="text-white/40 block mb-1">Message</span>
                          <p className="text-white/80">{quote.message}</p>
                        </div>
                      )}
                      
                      <div className="mt-4 text-xs text-white/30">
                        Reçue le {new Date(quote.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div data-testid="services-section">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold font-['Playfair_Display']">Services</h1>
                <button
                  onClick={() => setEditingService({ isNew: true, icon: 'Star', title: '', description: '', order: services.length + 1 })}
                  className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                  data-testid="add-service-btn"
                >
                  <Plus size={16} />
                  Ajouter
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => {
                  const IconComponent = iconMap[service.icon] || Star;
                  const isEditing = editingService?.id === service.id;
                  
                  return (
                    <div key={service.id} className="card-dark p-6" data-testid={`service-admin-${service.id}`}>
                      {isEditing ? (
                        <div className="space-y-4">
                          <div>
                            <label className="label-gold">Icône</label>
                            <select
                              value={editingService.icon}
                              onChange={(e) => setEditingService({ ...editingService, icon: e.target.value })}
                              className="input-dark w-full"
                            >
                              {iconOptions.map(icon => (
                                <option key={icon} value={icon}>{icon}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="label-gold">Titre</label>
                            <input
                              type="text"
                              value={editingService.title}
                              onChange={(e) => setEditingService({ ...editingService, title: e.target.value })}
                              className="input-dark w-full"
                            />
                          </div>
                          <div>
                            <label className="label-gold">Description</label>
                            <textarea
                              value={editingService.description}
                              onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                              rows={3}
                              className="input-dark w-full resize-none"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => saveService(editingService)} className="btn-gold flex-1 py-2 text-sm">
                              <Save size={14} className="inline mr-1" /> Sauver
                            </button>
                            <button onClick={() => setEditingService(null)} className="btn-outline flex-1 py-2 text-sm">
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                              <IconComponent className="text-[#D4AF37]" size={20} />
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setEditingService(service)}
                                className="p-2 text-white/40 hover:text-[#D4AF37] transition-colors"
                                data-testid={`edit-service-${service.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => deleteService(service.id)}
                                className="p-2 text-white/40 hover:text-red-400 transition-colors"
                                data-testid={`delete-service-${service.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          <h3 className="text-lg font-semibold font-['Playfair_Display'] mb-2">{service.title}</h3>
                          <p className="text-white/60 text-sm">{service.description}</p>
                        </>
                      )}
                    </div>
                  );
                })}
                
                {/* New Service Form */}
                {editingService?.isNew && (
                  <div className="card-dark p-6 border-[#D4AF37]/50" data-testid="new-service-form">
                    <div className="space-y-4">
                      <div>
                        <label className="label-gold">Icône</label>
                        <select
                          value={editingService.icon}
                          onChange={(e) => setEditingService({ ...editingService, icon: e.target.value })}
                          className="input-dark w-full"
                        >
                          {iconOptions.map(icon => (
                            <option key={icon} value={icon}>{icon}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="label-gold">Titre</label>
                        <input
                          type="text"
                          value={editingService.title}
                          onChange={(e) => setEditingService({ ...editingService, title: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Nom du service"
                        />
                      </div>
                      <div>
                        <label className="label-gold">Description</label>
                        <textarea
                          value={editingService.description}
                          onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                          rows={3}
                          className="input-dark w-full resize-none"
                          placeholder="Description du service"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveService(editingService)} className="btn-gold flex-1 py-2 text-sm">
                          <Plus size={14} className="inline mr-1" /> Créer
                        </button>
                        <button onClick={() => setEditingService(null)} className="btn-outline flex-1 py-2 text-sm">
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vehicles Tab */}
          {activeTab === 'vehicles' && (
            <div data-testid="vehicles-section">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold font-['Playfair_Display']">Véhicules</h1>
                <button
                  onClick={() => setEditingVehicle({ isNew: true, name: '', type: '', capacity: '', image_url: '', order: vehicles.length + 1 })}
                  className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                  data-testid="add-vehicle-btn"
                >
                  <Plus size={16} />
                  Ajouter
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => {
                  const isEditing = editingVehicle?.id === vehicle.id;
                  
                  return (
                    <div key={vehicle.id} className="card-dark overflow-hidden" data-testid={`vehicle-admin-${vehicle.id}`}>
                      {isEditing ? (
                        <div className="p-6 space-y-4">
                          <div>
                            <label className="label-gold">Nom</label>
                            <input
                              type="text"
                              value={editingVehicle.name}
                              onChange={(e) => setEditingVehicle({ ...editingVehicle, name: e.target.value })}
                              className="input-dark w-full"
                            />
                          </div>
                          <div>
                            <label className="label-gold">Type</label>
                            <input
                              type="text"
                              value={editingVehicle.type}
                              onChange={(e) => setEditingVehicle({ ...editingVehicle, type: e.target.value })}
                              className="input-dark w-full"
                              placeholder="Ex: Berline Confort"
                            />
                          </div>
                          <div>
                            <label className="label-gold">Capacité</label>
                            <input
                              type="text"
                              value={editingVehicle.capacity}
                              onChange={(e) => setEditingVehicle({ ...editingVehicle, capacity: e.target.value })}
                              className="input-dark w-full"
                              placeholder="Ex: 1-4 passagers"
                            />
                          </div>
                          <div>
                            <label className="label-gold">Image du véhicule</label>
                            {editingVehicle.image_url && (
                              <div className="mb-3 aspect-video bg-[#121212] rounded overflow-hidden">
                                <img 
                                  src={editingVehicle.image_url} 
                                  alt="Aperçu"
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}
                            <div className="flex gap-2">
                              <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept=".jpg,.jpeg,.png,.webp,.gif"
                                className="hidden"
                                id="vehicle-image-upload"
                              />
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="btn-gold flex-1 py-2 text-sm flex items-center justify-center gap-2"
                              >
                                <Upload size={14} />
                                {uploading ? 'Upload...' : 'Uploader une image'}
                              </button>
                            </div>
                            <p className="text-white/30 text-xs mt-2">ou entrez une URL :</p>
                            <input
                              type="text"
                              value={editingVehicle.image_url}
                              onChange={(e) => setEditingVehicle({ ...editingVehicle, image_url: e.target.value })}
                              className="input-dark w-full mt-1"
                              placeholder="https://..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => saveVehicle(editingVehicle)} className="btn-gold flex-1 py-2 text-sm">
                              <Save size={14} className="inline mr-1" /> Sauver
                            </button>
                            <button onClick={() => setEditingVehicle(null)} className="btn-outline flex-1 py-2 text-sm">
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="aspect-video bg-[#121212] relative group">
                            <img 
                              src={vehicle.image_url} 
                              alt={vehicle.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1567788701545-850832a506b8?crop=entropy&cs=srgb&fm=jpg&q=85';
                              }}
                            />
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setEditingVehicle(vehicle)}
                                className="p-2 bg-black/70 text-white hover:text-[#D4AF37] transition-colors"
                                data-testid={`edit-vehicle-${vehicle.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => deleteVehicle(vehicle.id)}
                                className="p-2 bg-black/70 text-white hover:text-red-400 transition-colors"
                                data-testid={`delete-vehicle-${vehicle.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="p-6 text-center">
                            <span className="label-gold">{vehicle.capacity}</span>
                            <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-1">{vehicle.name}</h3>
                            <p className="text-white/60 text-sm">{vehicle.type}</p>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                
                {/* New Vehicle Form */}
                {editingVehicle?.isNew && (
                  <div className="card-dark p-6 border-[#D4AF37]/50" data-testid="new-vehicle-form">
                    <div className="space-y-4">
                      <div>
                        <label className="label-gold">Nom</label>
                        <input
                          type="text"
                          value={editingVehicle.name}
                          onChange={(e) => setEditingVehicle({ ...editingVehicle, name: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Ex: Mercedes Classe S"
                        />
                      </div>
                      <div>
                        <label className="label-gold">Type</label>
                        <input
                          type="text"
                          value={editingVehicle.type}
                          onChange={(e) => setEditingVehicle({ ...editingVehicle, type: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Ex: Berline Luxe"
                        />
                      </div>
                      <div>
                        <label className="label-gold">Capacité</label>
                        <input
                          type="text"
                          value={editingVehicle.capacity}
                          onChange={(e) => setEditingVehicle({ ...editingVehicle, capacity: e.target.value })}
                          className="input-dark w-full"
                          placeholder="Ex: 1-4 passagers"
                        />
                      </div>
                      <div>
                        <label className="label-gold">Image du véhicule</label>
                        {editingVehicle.image_url && (
                          <div className="mb-3 aspect-video bg-[#121212] rounded overflow-hidden">
                            <img 
                              src={editingVehicle.image_url} 
                              alt="Aperçu"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept=".jpg,.jpeg,.png,.webp,.gif"
                            className="hidden"
                            id="new-vehicle-image-upload"
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="btn-gold flex-1 py-2 text-sm flex items-center justify-center gap-2"
                          >
                            <Upload size={14} />
                            {uploading ? 'Upload...' : 'Uploader une image'}
                          </button>
                        </div>
                        <p className="text-white/30 text-xs mt-2">ou entrez une URL :</p>
                        <input
                          type="text"
                          value={editingVehicle.image_url}
                          onChange={(e) => setEditingVehicle({ ...editingVehicle, image_url: e.target.value })}
                          className="input-dark w-full mt-1"
                          placeholder="https://..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveVehicle(editingVehicle)} className="btn-gold flex-1 py-2 text-sm">
                          <Plus size={14} className="inline mr-1" /> Créer
                        </button>
                        <button onClick={() => setEditingVehicle(null)} className="btn-outline flex-1 py-2 text-sm">
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && content && (
            <div data-testid="content-section">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-bold font-['Playfair_Display']">Contenu du site</h1>
                {!editingContent ? (
                  <button
                    onClick={() => setEditingContent(true)}
                    className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                    data-testid="edit-content-btn"
                  >
                    <Edit2 size={16} />
                    Modifier
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={saveContent}
                      className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                      data-testid="save-content-btn"
                    >
                      <Save size={16} />
                      Sauvegarder
                    </button>
                    <button
                      onClick={() => { setEditingContent(false); setContentForm(content); }}
                      className="btn-outline text-sm py-2 px-4"
                      data-testid="cancel-content-btn"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-8">
                {/* Hero Section */}
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-4 text-[#D4AF37]">Section Hero</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Titre principal</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.hero_title}
                          onChange={(e) => setContentForm({ ...contentForm, hero_title: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.hero_title}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-gold">Texte du bouton</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.hero_cta_text}
                          onChange={(e) => setContentForm({ ...contentForm, hero_cta_text: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.hero_cta_text}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-gold">Sous-titre</label>
                      {editingContent ? (
                        <textarea
                          value={contentForm.hero_subtitle}
                          onChange={(e) => setContentForm({ ...contentForm, hero_subtitle: e.target.value })}
                          rows={2}
                          className="input-dark w-full resize-none"
                        />
                      ) : (
                        <p className="text-white/80">{content.hero_subtitle}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-gold">Image de fond (Taille recommandée : 1920x1080 pixels)</label>
                      {contentForm.hero_image_url && (
                        <div className="mb-3 h-48 bg-[#121212] rounded overflow-hidden">
                          <img 
                            src={contentForm.hero_image_url} 
                            alt="Aperçu Hero"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      {editingContent ? (
                        <div>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="file"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const url = await uploadImage(file);
                                  if (url) {
                                    setContentForm({ ...contentForm, hero_image_url: url });
                                  }
                                }
                                e.target.value = '';
                              }}
                              accept=".jpg,.jpeg,.png,.webp"
                              className="hidden"
                              id="hero-image-upload"
                            />
                            <button
                              type="button"
                              onClick={() => document.getElementById('hero-image-upload').click()}
                              disabled={uploading}
                              className="btn-gold py-2 px-4 text-sm flex items-center gap-2"
                            >
                              <Upload size={14} />
                              {uploading ? 'Upload...' : 'Uploader une image'}
                            </button>
                          </div>
                          <p className="text-white/30 text-xs mb-2">ou entrez une URL :</p>
                          <input
                            type="text"
                            value={contentForm.hero_image_url || ''}
                            onChange={(e) => setContentForm({ ...contentForm, hero_image_url: e.target.value })}
                            className="input-dark w-full"
                            placeholder="https://..."
                          />
                        </div>
                      ) : (
                        <p className="text-white/60 text-sm truncate">{content.hero_image_url || 'Image par défaut'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services Section */}
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-4 text-[#D4AF37]">Section Services</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Titre</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.services_title}
                          onChange={(e) => setContentForm({ ...contentForm, services_title: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.services_title}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-gold">Sous-titre</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.services_subtitle}
                          onChange={(e) => setContentForm({ ...contentForm, services_subtitle: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.services_subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fleet Section */}
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-4 text-[#D4AF37]">Section Flotte</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Titre</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.fleet_title}
                          onChange={(e) => setContentForm({ ...contentForm, fleet_title: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.fleet_title}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-gold">Sous-titre</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.fleet_subtitle}
                          onChange={(e) => setContentForm({ ...contentForm, fleet_subtitle: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.fleet_subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-4 text-[#D4AF37]">Section Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Titre</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.contact_title}
                          onChange={(e) => setContentForm({ ...contentForm, contact_title: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.contact_title}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-gold">Sous-titre</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.contact_subtitle}
                          onChange={(e) => setContentForm({ ...contentForm, contact_subtitle: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.contact_subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-4 text-[#D4AF37]">Informations de contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="label-gold">Téléphone</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.phone}
                          onChange={(e) => setContentForm({ ...contentForm, phone: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.phone}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-gold">Email</label>
                      {editingContent ? (
                        <input
                          type="email"
                          value={contentForm.email}
                          onChange={(e) => setContentForm({ ...contentForm, email: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="label-gold">Adresse</label>
                      {editingContent ? (
                        <input
                          type="text"
                          value={contentForm.address}
                          onChange={(e) => setContentForm({ ...contentForm, address: e.target.value })}
                          className="input-dark w-full"
                        />
                      ) : (
                        <p className="text-white/80">{content.address}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="card-dark p-6">
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-4 text-[#D4AF37]">Pied de page</h3>
                  <div>
                    <label className="label-gold">Texte du footer</label>
                    {editingContent ? (
                      <input
                        type="text"
                        value={contentForm.footer_text}
                        onChange={(e) => setContentForm({ ...contentForm, footer_text: e.target.value })}
                        className="input-dark w-full"
                      />
                    ) : (
                      <p className="text-white/80">{content.footer_text}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
